import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Loader2, RotateCcw, CheckCircle2 } from "lucide-react";

const TR = {
  sk: {
    title: "Nespokojný? Žiadne otázky.",
    sub: "V rámci 14 dní zo zákona ti môžeme vrátiť peniaze plne automaticky. Klik, dôvod (nepovinné) a máš ich späť na karte do 5–10 dní.",
    cta: "Vrátiť peniaze",
    confirmTitle: "Vrátiť peniaze",
    confirmBody: "Vrátime ti {amount}{cur} z platby z {date}. Tvoj prístup okamžite klesne na FREE. Pokračovať?",
    reasonPh: "Krátky dôvod (nepovinné, ale veľmi nám pomôže) …",
    cancel: "Zrušiť",
    confirm: "Áno, vrátiť",
    lastWarning: "⚠ Pozor — toto je tvoj posledný refund tento rok. Po ňom už nebudeš môcť ďalšie peniaze vrátiť 12 mesiacov.",
    success: "Hotovo. Peniaze prídu na tvoju kartu do 5–10 pracovných dní. Vďaka za úprimnosť — sme tu, keď nás budeš znova hľadať.",
    err: "Chyba",
    trust: "Ďakujeme, že nám dôveruješ. Ak ti niečo nesedí — peniaze vrátime bez výhovoriek.",
    errReasons: {
      yearly_limit_reached: "Tento rok si už refund využil 2× — limit je vyčerpaný. Skúste o 12 mesiacov.",
      too_many_logins: "Refund je možný len ak si appku takmer nepoužíval (max 3 prihlásenia).",
      account_banned: "Účet je pozastavený — refund nie je možný.",
      no_eligible_payment: "Nenašli sme platbu mladšiu ako 14 dní, ktorú by bolo možné vrátiť.",
    },
  },
  en: {
    title: "Not happy? No questions asked.",
    sub: "Within 14 days by law we'll refund your money fully automatically. One click, a reason (optional), and you'll have it back on your card in 5–10 days.",
    cta: "Get a refund",
    confirmTitle: "Refund payment",
    confirmBody: "We'll refund {amount}{cur} from your payment on {date}. Your access drops to FREE immediately. Continue?",
    reasonPh: "Short reason (optional, but helps us a lot) …",
    cancel: "Cancel",
    confirm: "Yes, refund",
    lastWarning: "⚠ Heads up — this is your last refund this year. After this you can't refund again for 12 months.",
    success: "Done. The money will arrive on your card within 5–10 business days. Thanks for the honesty — we're here when you come back.",
    err: "Error",
    trust: "Thanks for trusting us. If something's off — we refund, no excuses.",
    errReasons: {
      yearly_limit_reached: "You've already used 2 refunds this year — the yearly limit is reached. Try again in 12 months.",
      too_many_logins: "Refund is only possible if you barely used the app (max 3 logins).",
      account_banned: "Account is suspended — refund unavailable.",
      no_eligible_payment: "We couldn't find a payment within the last 14 days to refund.",
    },
  },
  cs: {
    title: "Nespokojen? Bez otázek.",
    sub: "Do 14 dnů ze zákona vrátíme peníze plně automaticky. Klik, důvod (nepovinné), a máš je zpět na kartě do 5–10 dnů.",
    cta: "Vrátit peníze",
    confirmTitle: "Vrátit peníze",
    confirmBody: "Vrátíme ti {amount}{cur} z platby ze dne {date}. Tvůj přístup okamžitě klesne na FREE. Pokračovat?",
    reasonPh: "Krátký důvod (nepovinné, ale moc nám to pomůže) …",
    cancel: "Zrušit",
    confirm: "Ano, vrátit",
    lastWarning: "⚠ Pozor — toto je tvůj poslední refund tento rok. Po něm už nebudeš moci dalších 12 měsíců peníze vrátit.",
    success: "Hotovo. Peníze přijdou na tvou kartu během 5–10 pracovních dnů. Díky za upřímnost.",
    err: "Chyba",
    trust: "Děkujeme, že nám důvěřuješ. Když ti něco nesedí — peníze vrátíme bez výmluv.",
    errReasons: {
      yearly_limit_reached: "Tento rok jsi už refund využil 2× — limit je vyčerpán. Zkuste za 12 měsíců.",
      too_many_logins: "Refund je možný pouze pokud jsi aplikaci téměř nepoužíval (max 3 přihlášení).",
      account_banned: "Účet je pozastaven — refund není možný.",
      no_eligible_payment: "Nenašli jsme platbu mladší než 14 dní, kterou bychom mohli vrátit.",
    },
  },
  de: {
    title: "Unzufrieden? Keine Fragen.",
    sub: "Innerhalb von 14 Tagen erstatten wir gesetzlich vollautomatisch. Ein Klick, ein Grund (optional), und das Geld ist in 5–10 Tagen zurück.",
    cta: "Geld zurück",
    confirmTitle: "Zahlung erstatten",
    confirmBody: "Wir erstatten {amount}{cur} aus deiner Zahlung vom {date}. Dein Zugang fällt sofort auf FREE. Fortfahren?",
    reasonPh: "Kurzer Grund (optional, hilft uns aber sehr) …",
    cancel: "Abbrechen",
    confirm: "Ja, erstatten",
    lastWarning: "⚠ Achtung — das ist deine letzte Erstattung in diesem Jahr. Danach kannst du 12 Monate keine weitere Erstattung beantragen.",
    success: "Erledigt. Das Geld erscheint in 5–10 Werktagen auf deiner Karte. Danke für die Ehrlichkeit.",
    err: "Fehler",
    trust: "Danke für dein Vertrauen. Wenn etwas nicht passt — wir erstatten ohne Ausreden.",
    errReasons: {
      yearly_limit_reached: "Du hast in diesem Jahr bereits 2 Erstattungen genutzt — das Jahreslimit ist erreicht. Versuche es in 12 Monaten erneut.",
      too_many_logins: "Erstattung nur möglich, wenn du die App kaum genutzt hast (max. 3 Anmeldungen).",
      account_banned: "Konto gesperrt — Erstattung nicht möglich.",
      no_eligible_payment: "Keine Zahlung der letzten 14 Tage gefunden, die erstattet werden kann.",
    },
  },
  pl: {
    title: "Niezadowolony? Bez pytań.",
    sub: "W ciągu 14 dni z mocy prawa zwracamy pieniądze w pełni automatycznie. Klik, powód (opcjonalnie) i są z powrotem na karcie w 5–10 dni.",
    cta: "Zwrot pieniędzy",
    confirmTitle: "Zwrot płatności",
    confirmBody: "Zwrócimy ci {amount}{cur} z płatności z dnia {date}. Twój dostęp natychmiast spadnie do FREE. Kontynuować?",
    reasonPh: "Krótki powód (opcjonalnie, ale bardzo pomaga) …",
    cancel: "Anuluj",
    confirm: "Tak, zwróć",
    lastWarning: "⚠ Uwaga — to twój ostatni zwrot w tym roku. Po nim nie będziesz mógł odzyskać pieniędzy przez 12 miesięcy.",
    success: "Gotowe. Pieniądze trafią na twoją kartę w ciągu 5–10 dni roboczych. Dzięki za szczerość.",
    err: "Błąd",
    trust: "Dzięki, że nam ufasz. Jeśli coś nie pasuje — zwracamy bez wymówek.",
    errReasons: {
      yearly_limit_reached: "W tym roku wykorzystałeś już 2 zwroty — limit roczny osiągnięty. Spróbuj za 12 miesięcy.",
      too_many_logins: "Zwrot możliwy tylko jeśli prawie nie korzystałeś z aplikacji (max 3 logowania).",
      account_banned: "Konto zawieszone — zwrot niemożliwy.",
      no_eligible_payment: "Nie znaleźliśmy płatności z ostatnich 14 dni do zwrotu.",
    },
  },
  fr: {
    title: "Pas satisfait ? Aucune question.",
    sub: "Dans les 14 jours légaux, nous pouvons te rembourser entièrement et automatiquement. Un clic, une raison (facultatif) et tu récupères ton argent sur ta carte sous 5–10 jours.",
    cta: "Demander un remboursement",
    confirmTitle: "Demander un remboursement",
    confirmBody: "Nous te rembourserons {amount}{cur} du paiement du {date}. Ton accès repassera immédiatement en FREE. Continuer ?",
    reasonPh: "Brève raison (facultatif, mais ça nous aide beaucoup) …",
    cancel: "Annuler",
    confirm: "Oui, rembourser",
    lastWarning: "⚠ Attention — c'est ton dernier remboursement cette année. Après celui-ci, tu ne pourras plus en demander pendant 12 mois.",
    success: "Terminé. L'argent arrivera sur ta carte sous 5–10 jours ouvrés. Merci pour ta franchise — on est là si tu nous cherches à nouveau.",
    err: "Erreur",
    trust: "Merci de nous faire confiance. Si quelque chose ne va pas — on rembourse sans discuter.",
    errReasons: {
      yearly_limit_reached: "Tu as déjà utilisé 2 remboursements cette année — limite atteinte. Réessaie dans 12 mois.",
      too_many_logins: "Le remboursement n'est possible que si tu n'as presque pas utilisé l'app (max 3 connexions).",
      account_banned: "Compte suspendu — remboursement impossible.",
      no_eligible_payment: "Aucun paiement de moins de 14 jours éligible au remboursement.",
    },
  },
};

export default function RefundButton() {
  const { lang } = useLang() || { lang: "sk" };
  const { refresh } = useAuth() || {};
  const tr = TR[lang] || TR.sk;

  const [elig, setElig] = useState(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/billing/refund/eligibility")
      .then((r) => { if (!cancelled) setElig(r.data); })
      .catch(() => { if (!cancelled) setElig({ eligible: false }); });
    return () => { cancelled = true; };
  }, []);

  if (done) {
    return (
      <div data-testid="refund-success" className="border border-green-700 bg-green-950/30 p-5">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-200 leading-relaxed">{tr.success}</p>
        </div>
      </div>
    );
  }

  if (!elig || !elig.eligible) return null;

  const amount = typeof elig.amount === "number" ? elig.amount.toFixed(2) : elig.amount;
  const cur = (elig.currency || "eur").toUpperCase() === "EUR" ? "€" : ` ${(elig.currency || "").toUpperCase()}`;
  const date = elig.paid_at ? new Date(elig.paid_at).toLocaleDateString(lang === "sk" ? "sk-SK" : lang) : "";

  const doRefund = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/refund/request", { reason: reason.trim().slice(0, 300) });
      setDone(r.data);
      setOpen(false);
      try { if (typeof refresh === "function") await refresh(); } catch (e) { /* noop */ }
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      // Backend returns e.g. "Not eligible for refund: yearly_limit_reached"
      const reasonKey = (detail.match(/:\s*([a-z_]+)\s*$/i) || [])[1];
      const localized = reasonKey && tr.errReasons?.[reasonKey];
      alert(localized || detail || tr.err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-zinc-800 bg-[var(--wb-surface)] p-5 mb-6" data-testid="refund-card">
      <div className="flex items-start gap-3 mb-3">
        <RotateCcw className="w-5 h-5 text-zinc-500 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-display font-bold text-base text-zinc-200">{tr.title}</div>
          <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{tr.sub}</p>
        </div>
      </div>
      <button
        data-testid="refund-btn"
        onClick={() => setOpen(true)}
        className="wb-btn-ghost px-4 py-2 text-xs inline-flex items-center gap-2 !border-zinc-700 hover:!border-zinc-500"
      >
        <RotateCcw className="w-3 h-3" /> {tr.cta}
      </button>
      <p data-testid="refund-trust" className="text-[11px] text-zinc-600 italic mt-3 leading-relaxed border-l border-zinc-800 pl-3">
        {tr.trust}
      </p>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => !busy && setOpen(false)}>
          <div className="bg-zinc-900 border border-zinc-800 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="refund-modal">
            <h3 className="font-display font-bold text-lg mb-3 text-white">{tr.confirmTitle}</h3>
            <p className="text-sm text-zinc-300 mb-4 leading-relaxed">
              {tr.confirmBody.replace("{amount}", amount).replace("{cur}", cur).replace("{date}", date)}
            </p>
            {(elig.refunds_used || 0) >= 1 && (
              <div data-testid="refund-last-warning" className="border-l-2 border-amber-600 bg-amber-950/30 p-3 mb-4 text-xs text-amber-200 font-mono">
                {tr.lastWarning}
              </div>
            )}
            <textarea
              data-testid="refund-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={tr.reasonPh}
              maxLength={300}
              rows={3}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-zinc-600 focus:outline-none text-sm p-2 font-mono mb-4"
            />
            <div className="flex gap-2 justify-end">
              <button
                data-testid="refund-cancel"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="wb-btn-ghost px-4 py-2 text-xs"
              >
                {tr.cancel}
              </button>
              <button
                data-testid="refund-confirm"
                onClick={doRefund}
                disabled={busy}
                className="wb-btn-primary px-4 py-2 text-xs inline-flex items-center gap-2"
              >
                {busy && <Loader2 className="w-3 h-3 animate-spin" />} {tr.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
